import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Check, X, MinusCircle, ClipboardCheck } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Input, Textarea } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { api, fileToPhotoPayload } from "@/lib/api";

const SEVERITIES = [
  { value: "advisory", label: "Advisory" },
  { value: "minor", label: "Minor" },
  { value: "major", label: "Major" },
  { value: "safety", label: "Safety critical" },
];

function PhotoField({ testId, file, onPick, required }) {
  return (
    <label data-testid={testId} className={`mt-2 flex items-center gap-2 h-11 px-3 rounded-[6px] border text-sm cursor-pointer ${file ? "border-primary text-primary bg-primary/5" : required ? "border-[hsl(var(--vda-amber))] text-[hsl(var(--vda-amber))]" : "border-[hsl(var(--input))] text-muted-foreground"}`}>
      <Camera className="h-4 w-4" />
      {file ? file.name.slice(0, 24) : (required ? "Photo required — tap to capture" : "Add a photo (optional)")}
      <input type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </label>
  );
}

export default function DriverVehicleCheck() {
  const nav = useNavigate();
  const { data: cfg, loading } = useAsync(() => api.driverVehicleCheckConfig(), []);
  const [odometer, setOdometer] = useState("");
  const [odoFile, setOdoFile] = useState(null);
  const [items, setItems] = useState({}); // key -> {result, severity, notes, file}
  const [saving, setSaving] = useState(false);

  if (loading) return <DriverShell><Spinner /></DriverShell>;

  if (!cfg?.enabled) {
    return (
      <DriverShell>
        <div className="py-10 text-center" data-testid="check-unavailable">
          <ClipboardCheck className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-lg font-semibold">Weekly vehicle check</h1>
          <p className="text-sm text-muted-foreground mt-1">This isn't part of your fleet's setup right now.</p>
          <Btn variant="outline" className="mt-4" onClick={() => nav("/driver")}>Back to home</Btn>
        </div>
      </DriverShell>
    );
  }

  const template = cfg.template || [];
  const setItem = (key, patch) => setItems((s) => ({ ...s, [key]: { result: "ok", ...s[key], ...patch } }));
  const resultOf = (key) => items[key]?.result || "ok";

  const submit = async () => {
    // Client validation of mandatory photos
    for (const t of template) {
      if (t.requires_photo && !items[t.key]?.file) {
        toast.error(`A photo is required for ${t.label}.`);
        return;
      }
    }
    if (!odoFile) { toast.error("A dashboard/odometer photo is required."); return; }
    setSaving(true);
    try {
      const odoPayload = await fileToPhotoPayload(odoFile);
      const itemPayloads = await Promise.all(template.map(async (t) => {
        const it = items[t.key] || {};
        const p = it.file ? await fileToPhotoPayload(it.file) : null;
        return {
          item_key: t.key, result: it.result || "ok",
          severity: it.result === "defect" ? (it.severity || "minor") : null,
          notes: it.notes || null, photo_base64: p ? p.data_base64 : null,
        };
      }));
      const res = await api.driverSubmitVehicleCheck({
        odometer_mileage: odometer ? Number(odometer) : null,
        odometer_photo_base64: odoPayload.data_base64,
        items: itemPayloads,
      });
      if (res.defect_count > 0) toast.success(`Check submitted. ${res.defect_count} defect(s) raised with your Venson team.`);
      else toast.success("Check submitted — no defects. Thank you.");
      nav("/driver");
    } catch (e) {
      toast.error(e.message || "Could not submit the check.");
    } finally {
      setSaving(false);
    }
  };

  const RESULTS = [
    { v: "ok", label: "OK", icon: Check, tone: "text-primary border-primary bg-primary/10" },
    { v: "defect", label: "Defect", icon: X, tone: "text-[hsl(var(--vda-red))] border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10" },
    { v: "na", label: "N/A", icon: MinusCircle, tone: "text-muted-foreground border-muted-foreground bg-secondary" },
  ];

  return (
    <DriverShell>
      <div data-testid="driver-vehicle-check">
        <p className="vda-tick text-[hsl(var(--vda-quiet))]">Weekly check</p>
        <h1 className="text-xl font-semibold tracking-tight mb-1">Vehicle check</h1>
        <p className="text-sm text-muted-foreground mb-4">Walk around your vehicle and record each item. Tyre and dashboard photos are required.</p>

        <Panel className="p-4 mb-4">
          <label className="text-xs font-semibold">Current mileage</label>
          <Input data-testid="check-odometer" type="number" inputMode="numeric" value={odometer}
            onChange={(e) => setOdometer(e.target.value)} placeholder="e.g. 24500" className="mt-1" />
          <PhotoField testId="check-odo-photo" file={odoFile} onPick={setOdoFile} required />
        </Panel>

        <div className="space-y-3">
          {template.map((t) => {
            const r = resultOf(t.key);
            return (
              <Panel key={t.key} className="p-4" data-testid={`check-item-${t.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <strong className="block text-sm font-semibold">{t.label}</strong>
                    <small className="text-[11px] text-muted-foreground uppercase">{t.category}</small>
                  </div>
                  <div className="flex gap-1.5">
                    {RESULTS.map((res) => (
                      <button key={res.v} data-testid={`check-${t.key}-${res.v}`}
                        onClick={() => setItem(t.key, { result: res.v })}
                        className={`h-9 px-2.5 inline-flex items-center gap-1 rounded-[6px] border text-xs font-medium transition-colors ${r === res.v ? res.tone : "border-[hsl(var(--input))] text-muted-foreground"}`}>
                        <res.icon className="h-3.5 w-3.5" />{res.label}
                      </button>
                    ))}
                  </div>
                </div>
                {r === "defect" && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {SEVERITIES.map((s) => (
                        <button key={s.value} data-testid={`check-${t.key}-sev-${s.value}`}
                          onClick={() => setItem(t.key, { severity: s.value })}
                          className={`h-8 px-2.5 rounded-[6px] border text-xs ${items[t.key]?.severity === s.value ? "border-primary text-primary bg-primary/10" : "border-[hsl(var(--input))] text-muted-foreground"}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <Textarea data-testid={`check-${t.key}-notes`} rows={2} placeholder="Describe the defect"
                      value={items[t.key]?.notes || ""} onChange={(e) => setItem(t.key, { notes: e.target.value })} />
                  </div>
                )}
                {(t.requires_photo || r === "defect") && (
                  <PhotoField testId={`check-${t.key}-photo`} file={items[t.key]?.file}
                    onPick={(f) => setItem(t.key, { file: f })} required={t.requires_photo} />
                )}
              </Panel>
            );
          })}
        </div>

        <Btn data-testid="check-submit" className="w-full mt-5" onClick={submit} disabled={saving}>
          {saving ? "Submitting…" : "Submit vehicle check"}
        </Btn>
      </div>
    </DriverShell>
  );
}
